<template>
  <div class="intelligence-settings">
    <div class="intelligence-settings__intro">
      <div class="intelligence-settings__intro-heading">
        <h3 class="f14 fw500">Response settings</h3>
        <div class="intelligence-settings__intro-actions">
          <button
            class="f11"
            :class="{'intelligence-settings__status-button--active': statusesOpen}"
            type="button"
            @click="statusesOpen = !statusesOpen"
          >
            <v-icon size="15" color="#757575">mdi-progress-clock</v-icon>
            Statuses
          </button>
          <button class="f11" type="button" @click="$emit('restore-defaults')">
            Restore defaults
          </button>
        </div>
      </div>
      <p class="f11">Changes are saved automatically in this browser.</p>
    </div>

    <section v-if="statusesOpen" class="intelligence-settings__statuses">
      <div class="intelligence-settings__statuses-heading">
        <h4 class="f12 fw500">Thinking statuses</h4>
        <button type="button" aria-label="Hide thinking statuses" @click="statusesOpen = false">
          <v-icon size="18" color="#757575">mdi-close</v-icon>
        </button>
      </div>
      <div class="intelligence-settings__statuses-content">
        <p class="intelligence-settings__hint f11">
          Enter one status per line. Use <code>{keyword}</code> to insert the matched keyword.
        </p>

        <label class="intelligence-settings__field f11">
          <span>Common statuses for Keyword responses</span>
          <textarea
            :value="joinStatuses(settings.keywordStatuses)"
            rows="3"
            @input="updateKeywordStatuses($event.target.value)"
          />
        </label>

        <label
          v-for="(suggestion, index) in settings.suggestions"
          :key="`statuses-${suggestion.id}`"
          class="intelligence-settings__field intelligence-settings__status-field f11"
        >
          <span>{{ suggestion.prompt }}</span>
          <textarea
            :value="joinStatuses(suggestion.statuses)"
            rows="3"
            @input="updateSuggestionStatuses(index, $event.target.value)"
          />
        </label>
      </div>
    </section>

    <section class="intelligence-settings__section">
      <h4 class="f12 fw500">Suggested questions</h4>
      <article
        v-for="(suggestion, index) in settings.suggestions"
        :key="suggestion.id"
        class="intelligence-settings__card"
      >
        <label class="intelligence-settings__field f11">
          <span>Question {{ index + 1 }}</span>
          <input
            :value="suggestion.prompt"
            type="text"
            @input="updateSuggestion(index, 'prompt', $event.target.value)"
          />
        </label>
        <label class="intelligence-settings__field f11">
          <span>Answer</span>
          <textarea
            :value="suggestion.response"
            rows="3"
            @input="updateSuggestion(index, 'response', $event.target.value)"
          />
        </label>
      </article>
    </section>

    <section class="intelligence-settings__section">
      <div class="intelligence-settings__section-heading">
        <div>
          <h4 class="f12 fw500">Keyword responses</h4>
          <p class="f11">The first matching keyword determines the answer.</p>
        </div>
        <button
          class="intelligence-settings__add"
          type="button"
          aria-label="Add keyword response"
          @click="addKeywordResponse"
        >
          <v-icon size="18" color="#757575">mdi-plus</v-icon>
          <span class="f11">Add</span>
        </button>
      </div>

      <p v-if="!settings.keywordResponses.length" class="intelligence-settings__empty f12">
        Add a keyword and the answer that should be shown when it appears in a message.
      </p>

      <article
        v-for="(item, index) in settings.keywordResponses"
        :key="item.id"
        class="intelligence-settings__card intelligence-settings__card--keyword"
      >
        <button
          class="intelligence-settings__delete"
          type="button"
          aria-label="Delete keyword response"
          @click="removeKeywordResponse(index)"
        >
          <v-icon size="17" color="#9E9E9E">mdi-delete-outline</v-icon>
        </button>
        <label class="intelligence-settings__field f11">
          <span>Keyword</span>
          <input
            :value="item.keyword"
            type="text"
            placeholder="project"
            @input="updateKeywordResponse(index, 'keyword', $event.target.value)"
          />
        </label>
        <label class="intelligence-settings__field f11">
          <span>Answer</span>
          <textarea
            :value="item.response"
            rows="3"
            placeholder="Prepared answer for messages containing this keyword"
            @input="updateKeywordResponse(index, 'response', $event.target.value)"
          />
        </label>
      </article>
    </section>

  </div>
</template>

<script>
export default {
  name: 'ProAppIntelligenceSettings',
  props: {
    settings: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    nextKeywordId: 1,
    statusesOpen: false,
  }),
  methods: {
    joinStatuses(statuses) {
      return Array.isArray(statuses) ? statuses.join('\n') : ''
    },
    emitSettings(changes) {
      this.$emit('change', {...this.settings, ...changes})
    },
    updateSuggestion(index, field, value) {
      const suggestions = this.settings.suggestions.map((suggestion, itemIndex) =>
        itemIndex === index ? {...suggestion, [field]: value} : suggestion,
      )
      this.emitSettings({suggestions})
    },
    updateSuggestionStatuses(index, value) {
      const statuses = value.split('\n')
      const suggestions = this.settings.suggestions.map((suggestion, itemIndex) =>
        itemIndex === index ? {...suggestion, statuses} : suggestion,
      )
      this.emitSettings({suggestions})
    },
    addKeywordResponse() {
      const keywordResponses = [
        ...this.settings.keywordResponses,
        {
          id: `keyword-custom-${Date.now()}-${this.nextKeywordId++}`,
          keyword: '',
          response: '',
        },
      ]
      this.emitSettings({keywordResponses})
    },
    updateKeywordResponse(index, field, value) {
      const keywordResponses = this.settings.keywordResponses.map((item, itemIndex) =>
        itemIndex === index ? {...item, [field]: value} : item,
      )
      this.emitSettings({keywordResponses})
    },
    removeKeywordResponse(index) {
      const keywordResponses = this.settings.keywordResponses.filter(
        (item, itemIndex) => itemIndex !== index,
      )
      this.emitSettings({keywordResponses})
    },
    updateKeywordStatuses(value) {
      this.emitSettings({keywordStatuses: value.split('\n')})
    },
  },
}
</script>

<style lang="scss" scoped>
.intelligence-settings {
  min-height: 0;
  padding: 14px 16px 20px;
  overflow-y: auto;
  color: var(--v-secondary-darken4);
  background: var(--v-secondary-lighten5);
  scrollbar-width: thin;

  &__intro-heading,
  &__section-heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
  }

  &__intro {
    margin-bottom: 16px;

    h3,
    > p {
      margin: 0;
    }

    > p {
      width: 100%;
      margin-top: 4px;
      color: var(--v-secondary-darken1);
    }

    button {
      flex: 0 0 auto;
      padding: 5px 8px;
      border-radius: 6px;
      color: var(--v-secondary-darken1);
      background: white;
    }
  }

  &__intro-actions {
    display: flex;
    align-items: center;
    gap: 5px;

    button {
      display: flex;
      align-items: center;
      gap: 3px;
      white-space: nowrap;
    }
  }

  &__status-button--active {
    background: var(--v-secondary-lighten3) !important;
  }

  &__section + &__section {
    margin-top: 18px;
  }

  &__section > h4,
  &__section-heading h4,
  &__section-heading p {
    margin: 0;
  }

  &__section > h4 {
    margin-bottom: 7px;
  }

  &__section-heading {
    margin-bottom: 7px;

    p {
      margin-top: 2px;
      color: var(--v-secondary-darken1);
    }
  }

  &__card {
    position: relative;
    display: grid;
    gap: 7px;
    padding: 10px;
    border: 1px solid var(--v-secondary-lighten3);
    border-radius: 8px;
    background: white;

    & + & {
      margin-top: 8px;
    }

    &--keyword {
      padding-right: 38px;
    }
  }

  &__field {
    display: grid;
    gap: 3px;

    > span {
      color: var(--v-secondary-darken1);
    }

    input,
    textarea {
      width: 100%;
      padding: 6px 8px;
      border: 1px solid var(--v-secondary-lighten3);
      border-radius: 6px;
      outline: none;
      color: var(--v-secondary-darken4);
      background: white;
      font: inherit;
      line-height: 1.35;
      transition: border-color 0.2s ease;

      &:focus {
        border-color: var(--v-primary-base);
      }
    }

    textarea {
      min-height: 58px;
      resize: vertical;
    }
  }

  &__add {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: 2px;
    padding: 4px 7px;
    border-radius: 6px;
    color: var(--v-secondary-darken1);
    background: white;
  }

  &__delete {
    position: absolute;
    top: 7px;
    right: 7px;
    width: 26px;
    height: 26px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;

    &:hover,
    &:focus-visible {
      background: var(--v-secondary-lighten4);
    }
  }

  &__empty {
    margin: 0;
    padding: 12px;
    border: 1px dashed var(--v-secondary-lighten2);
    border-radius: 8px;
    color: var(--v-secondary-darken1);
    background: white;
  }

  &__statuses {
    margin-bottom: 18px;
    border: 1px solid var(--v-secondary-lighten3);
    border-radius: 8px;
    background: white;
  }

  &__statuses-heading {
    min-height: 40px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 10px;
    color: var(--v-secondary-darken3);

    h4 {
      margin: 0;
    }

    button {
      width: 26px;
      height: 26px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;

      &:hover,
      &:focus-visible {
        background: var(--v-secondary-lighten4);
      }
    }
  }

  &__statuses-content {
    display: grid;
    gap: 10px;
    padding: 0 10px 10px;
    border-top: 1px solid var(--v-secondary-lighten4);
  }

  &__hint {
    margin: 9px 0 0;
    color: var(--v-secondary-darken1);

    code {
      color: var(--v-primary-darken1);
    }
  }

  &__status-field {
    padding-top: 9px;
    border-top: 1px solid var(--v-secondary-lighten4);

    > span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}
</style>
